import type { ReactNode } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BackChevron } from '@/components/BackChevron';
import { Overline } from '@/components/Overline';
import { colors, radii, spacing } from '@/constants/theme';

export const ORDER_STEPS = 3;

export type OrderShellProps = {
  /** Defaults to leaving the screen. */
  onBack?: () => void;
  /** Where back goes, named in the link (the Figma's "‹ View Deal", "‹ Select Product"). */
  backLabel?: string;
  /** 1-based step of ORDER_STEPS and what it asks; omit for the loading, error and can't-order states. */
  progress?: { step: number; label: string };
  children: ReactNode;
  /** Pinned under the scroll (the step's one button). */
  footer?: ReactNode;
  testID?: string;
};

/**
 * The frame every step of the product order shares (Figma "Order 1-3"):
 * back, a three-segment progress bar with "STEP n OF 3" over it, the step's
 * content scrolling above a pinned button.
 */
export function OrderShell({ onBack, backLabel = 'View Deal', progress, children, footer, testID }: OrderShellProps) {
  return (
    <SafeAreaView edges={['top', 'bottom']} style={styles.safe} testID={testID}>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={styles.flex} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
          <BackChevron onPress={onBack} label={backLabel} testID="voltr-order-back" />
          {progress ? (
            <View
              style={styles.progress}
              accessible
              accessibilityRole="progressbar"
              accessibilityLabel={`Step ${progress.step} of ${ORDER_STEPS}: ${progress.label}`}
              testID="voltr-order-progress"
            >
              <View style={styles.segments}>
                {Array.from({ length: ORDER_STEPS }, (_, i) => (
                  <View key={i} style={[styles.segment, i < progress.step && styles.segmentDone]} />
                ))}
              </View>
              <Overline>{`Step ${progress.step} of ${ORDER_STEPS} - ${progress.label}`}</Overline>
            </View>
          ) : null}
          {children}
        </ScrollView>
        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  flex: { flex: 1 },
  content: { padding: spacing.lg, gap: spacing.lg },
  progress: { gap: spacing.sm },
  segments: { flexDirection: 'row', gap: spacing.xs },
  segment: { flex: 1, height: 6, borderRadius: radii.full, backgroundColor: colors.track },
  segmentDone: { backgroundColor: colors.electricGreen },
  footer: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
  },
});
